import type { TicketState } from './palette.ts';

/** HTML5 payload type. `text/plain` is also set so the gesture is a move, not a URL drag. */
export const BOARD_DRAG_MIME = 'application/x-nightshift-ticket';

const REFINING_ACTIVITIES = ['Standard', 'Research', 'Planning'] as const;
export type RefiningActivity = (typeof REFINING_ACTIVITIES)[number];

export function isRefiningActivity(value: string | undefined): value is RefiningActivity {
  return value !== undefined && (REFINING_ACTIVITIES as readonly string[]).includes(value);
}

const IN_FLIGHT: readonly TicketState[] = [
  'Refining',
  'Implementing',
  'GitLab CI',
  'AI Review',
  'Human Review',
  'Merging',
];

function isInFlightState(state: TicketState): boolean {
  return IN_FLIGHT.includes(state);
}

export type BoardDropTarget =
  | { kind: 'column'; key: TicketState; activity?: RefiningActivity }
  | { kind: 'waiting'; reason?: string }
  | { kind: 'cancelled' };

export type BoardDragCommand =
  | { type: 'start'; activity?: RefiningActivity }
  | { type: 'record_done' }
  | { type: 'shelve' }
  | { type: 'hold' }
  | { type: 'cancel' }
  | { type: 'restore' }
  | { type: 'resume' };

export interface BoardDragPayload {
  tickets?: readonly Omit<BoardDragPayload, 'tickets'>[];
  ticketKey: string;
  fromState: TicketState;
  /** Waiting tickets only. Resume may land only here. */
  resumeState?: TicketState | null;
}

export interface BoardDropOpts {
  resumeState?: TicketState | null;
}

/**
 * Human-legal board drops. Each result is a ticket command — never a free-form state write.
 *
 * Allowed:
 * - Backlog → Refining (start; group drop names the activity)
 * - Backlog → Done (record_done; historic work, no pipeline)
 * - Backlog → Cancelled
 * - in-flight → Backlog (shelve), Waiting/Manual Hold (hold), Cancelled
 * - Waiting → Backlog (shelve), resume-target column, Cancelled
 * - Cancelled → Backlog (restore)
 *
 * Refused: forward pipeline columns, send-back (needs a note), merge,
 * Human Review → Done, and every waiting reason except Manual Hold.
 */
export function boardDropCommand(
  from: TicketState,
  target: BoardDropTarget,
  opts: BoardDropOpts = {},
): BoardDragCommand | null {
  if (from === 'Done') return null;

  if (target.kind === 'cancelled') {
    return from === 'Cancelled' ? null : { type: 'cancel' };
  }

  if (target.kind === 'waiting') {
    if (target.reason !== undefined && target.reason !== 'manual_hold') return null;
    if (!isInFlightState(from)) return null;
    return { type: 'hold' };
  }

  const to = target.key;
  if (from === to && (to !== 'Refining' || target.activity === undefined)) {
    return null;
  }

  if (from === 'Backlog') {
    if (to === 'Refining') {
      return { type: 'start', ...(target.activity ? { activity: target.activity } : {}) };
    }
    if (to === 'Done') return { type: 'record_done' };
    return null;
  }

  if (from === 'Cancelled') {
    return to === 'Backlog' ? { type: 'restore' } : null;
  }

  if (from === 'Waiting') {
    if (to === 'Backlog') return { type: 'shelve' };
    if (opts.resumeState && to === opts.resumeState) return { type: 'resume' };
    return null;
  }

  if (isInFlightState(from)) {
    if (to === 'Backlog') return { type: 'shelve' };
    return null;
  }

  return null;
}

/** Columns and rails that should light up while this card is held. */
export function boardDragTargets(from: TicketState, resumeState?: TicketState | null): BoardDropTarget[] {
  switch (from) {
    case 'Backlog':
      return [{ kind: 'column', key: 'Refining' }, { kind: 'column', key: 'Done' }, { kind: 'cancelled' }];
    case 'Cancelled':
      return [{ kind: 'column', key: 'Backlog' }];
    case 'Waiting': {
      const targets: BoardDropTarget[] = [{ kind: 'column', key: 'Backlog' }, { kind: 'cancelled' }];
      if (resumeState && resumeState !== 'Backlog' && resumeState !== 'Cancelled') {
        targets.push({ kind: 'column', key: resumeState });
      }
      return targets;
    }
    case 'Done':
      return [];
    default:
      return [
        { kind: 'column', key: 'Backlog' },
        { kind: 'waiting', reason: 'manual_hold' },
        { kind: 'cancelled' },
      ];
  }
}

export function dropTargetAccepts(
  from: TicketState,
  target: BoardDropTarget,
  opts: BoardDropOpts = {},
): boolean {
  return boardDropCommand(from, target, opts) !== null;
}

export const BOARD_HOLD_REASON = 'Held from the board';
